import * as React from "react"
import * as ScrollAreaPrimitive from "@radix-ui/react-scroll-area"
import { motion } from "framer-motion"

import { cn } from "@/lib/utils"

import KanbanSkeleton from "@/components/ui/KanbanSkeleton"

const ScrollArea = React.forwardRef(({ className, children, ...props }, ref) => (
  <ScrollAreaPrimitive.Root
    ref={ref}
    className={cn("relative overflow-hidden", className)}
    {...props}>
    <ScrollAreaPrimitive.Viewport className="h-full w-full rounded-[inherit]">
      {children}
    </ScrollAreaPrimitive.Viewport>
    <ScrollBar />
    <ScrollAreaPrimitive.Corner />
  </ScrollAreaPrimitive.Root>
))
ScrollArea.displayName = ScrollAreaPrimitive.Root.displayName

const ScrollBar = React.forwardRef(({ className, orientation = "vertical", ...props }, ref) => (
  <ScrollAreaPrimitive.ScrollAreaScrollbar
    ref={ref}
    orientation={orientation}
    className={cn(
      "flex touch-none select-none transition-colors",
      orientation === "vertical" &&
        "h-full w-2.5 border-l border-l-transparent p-[1px]",
      orientation === "horizontal" &&
        "h-2.5 flex-col border-t border-t-transparent p-[1px]",
      className
    )}
    {...props}>
    <ScrollAreaPrimitive.ScrollAreaThumb className="relative flex-1 rounded-full bg-border" />
  </ScrollAreaPrimitive.ScrollAreaScrollbar>
))
ScrollBar.displayName = ScrollAreaPrimitive.ScrollAreaScrollbar.displayName

const ScrollImageFader = ({ 
  sections, 
  images, 
  components,
  backgroundClasses = [],
  aspectRatio = "aspect-video", 
  className = "",
  imageClassName = "",
  contentClassName = "",
  fullWidth = false,
  ...props 
}) => {
  const [currentImage, setCurrentImage] = React.useState(0);
  const containerRef = React.useRef(null);

  // Use either images or components, prioritizing components
  const displayItems = components || images;

  React.useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current) return;

      const containerRect = containerRef.current.getBoundingClientRect();
      const containerTop = containerRect.top;
      const containerBottom = containerRect.bottom;
      const windowHeight = window.innerHeight;

      // Calculate current section based on scroll position
      if (containerTop <= 0 && containerBottom > windowHeight) {
        // Component is actively scrolling through viewport
        const progress = Math.abs(containerTop) / (containerRect.height - windowHeight);
        const sectionIndex = Math.floor(progress * sections.length);
        const clampedIndex = Math.max(0, Math.min(sectionIndex, sections.length - 1));
        
        if (clampedIndex !== currentImage) {
          setCurrentImage(clampedIndex);
        }
      } else if (containerBottom <= windowHeight && containerTop < windowHeight) {
        // Component has been scrolled past, show the last image
        setCurrentImage(sections.length - 1);
      }
    };

    // Use requestAnimationFrame for smoother updates
    let ticking = false;
    const smoothScrollHandler = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', smoothScrollHandler, { passive: true });
    handleScroll(); // Initial check

    return () => {
      window.removeEventListener('scroll', smoothScrollHandler);
    };
  }, [sections.length, currentImage]);

  return (
    <div 
      ref={containerRef}
      className={cn("relative", className)} 
      style={{ height: `${sections.length * 75}vh` }}
      {...props}
    >
      {/* Background color layers */}
      {backgroundClasses.length > 0 && (
        <div className="absolute inset-0">
          {backgroundClasses.map((color, index) => {
            // Format color to include -500 if no number is present
            const formattedColor = color.includes('-') ? color : `${color}-500`;
            return (
              <div
                key={index}
                className={cn(
                  "absolute inset-0",
                  "transition-opacity duration-1000 ease-in-out",
                  `${formattedColor}`
                )}
                style={{
                  opacity: index <= currentImage ? 1 : 0
                }}
              />
            );
          })}
        </div>
      )}

      <div className={cn("relative", !fullWidth && "container mx-auto")}>
        {/* Sticky content container - either images or components */}
        <div 
          className={cn(
            "sticky top-0 right-0 h-[75vh] w-1/2 flex items-center justify-center p-8",
            "float-right"
          )}
        >
          <div className={cn("relative w-full max-w-2xl", aspectRatio)}>
            {displayItems && displayItems.map((item, index) => {
              // Check if item is a React component/element or an image URL
              const isComponent = React.isValidElement(item) || typeof item === 'function';
              
              return isComponent ? (
                <div
                  key={index}
                  className={cn(
                    "absolute inset-0 w-full h-full flex items-center justify-center",
                    "transition-opacity duration-1000 ease-in-out",
                    index === currentImage ? 'opacity-100' : 'opacity-0',
                    imageClassName
                  )}
                >
                  {typeof item === 'function' ? item() : item}
                </div>
              ) : (
                <img
                  key={index}
                  src={item}
                  alt=""
                  className={cn(
                    "absolute inset-0 w-full h-full object-cover rounded-lg",
                    "transition-opacity duration-1000 ease-in-out",
                    index === currentImage ? 'opacity-100' : 'opacity-0',
                    imageClassName
                  )}
                />
              );
            })}
          </div>
        </div>

        {/* Content sections */}
        <div className={cn("w-1/2 relative", contentClassName)}>
          {sections.map((section, index) => (
            <div key={index} className="flex items-center relative" style={{ height: '75vh' }}>
              {/* Section content */}
              <div className="relative z-20 py-10 px-4 -ml-4 rounded">
                {section}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const ScrollKanbanAnimator = ({ 
  sections, 
  kanbanProps,
  layoutTransition,
  className = "",
  contentClassName = "",
  fullWidth = false,
  initialColors = ["slate", "violet", "blue", "emerald", "orange", "rose"],
  endingColor = "violet",
  ...props 
}) => {
  const [scrollProgress, setScrollProgress] = React.useState(0);
  const [currentKanbanProps, setCurrentKanbanProps] = React.useState(null);
  const containerRef = React.useRef(null);

  React.useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current) return;

      const containerRect = containerRef.current.getBoundingClientRect();
      const containerTop = containerRect.top;
      const containerBottom = containerRect.bottom;
      const windowHeight = window.innerHeight;

      if (containerTop <= 0 && containerBottom > windowHeight) {
        const scrollableHeight = containerRect.height - windowHeight;
        const progress = Math.abs(containerTop) / scrollableHeight;
        setScrollProgress(Math.max(0, Math.min(1, progress)));
      } else if (containerBottom <= windowHeight && containerTop < windowHeight) {
        setScrollProgress(1);
      } else if (containerTop > 0) {
        setScrollProgress(0);
      }
    };

    // Throttled scroll handling for better performance
    let ticking = false;
    const throttledScrollHandler = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', throttledScrollHandler, { passive: true });
    handleScroll();

    return () => {
      window.removeEventListener('scroll', throttledScrollHandler);
    };
  }, []);

  // Calculate kanban props based on scroll progress with smooth transitions
  React.useEffect(() => {
    // Use a smooth easing function for better transitions
    const easeInOutCubic = (t) => {
      return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
    };

    const smoothProgress = easeInOutCubic(scrollProgress);
    
    // Smooth interpolation for columns: 6 -> 3
    const minColumns = 3;
    const maxColumns = 6;
    const columns = Math.round(maxColumns - (smoothProgress * (maxColumns - minColumns)));
    
    const items = 12;
    
    // Use colorArray for initial scattered state, gradually transition to organized color scheme
    let colorScheme = endingColor;
    let colorArray = null;
    
    // Use colorArray when progress is less than 60%, with smoother fade out
    if (scrollProgress < 0.6) {
      colorArray = initialColors;
    }
    
    const newProps = {
      ...kanbanProps,
      columns,
      items,
      colorScheme,
      colorArray,
      scrollProgress: smoothProgress // Pass smooth progress for any internal animations
    };

    // Only update if there's a meaningful change to prevent unnecessary re-renders
    setCurrentKanbanProps(prev => {
      if (!prev || 
          prev.columns !== newProps.columns || 
          prev.colorScheme !== newProps.colorScheme ||
          (prev.colorArray === null) !== (newProps.colorArray === null) ||
          Math.abs(prev.scrollProgress - newProps.scrollProgress) > 0.01) {
        return newProps;
      }
      return prev;
    });
  }, [scrollProgress, kanbanProps, endingColor, initialColors]);

  // Calculate which section should be highlighted based on scroll progress
  const getCurrentSectionIndex = () => {
    const sectionProgress = scrollProgress * (sections.length - 1);
    return Math.round(sectionProgress);
  };

  if (!currentKanbanProps) {
    // Initial props for first render
    return (
      <div 
        ref={containerRef}
        className={cn("relative", className)} 
        style={{ height: `${sections.length * 75}vh` }}
        {...props}
      >
        <div className={cn("relative", !fullWidth && "container mx-auto")}>        
          <div 
            className={cn(
              "sticky top-0 right-0 h-[75vh] w-full flex items-center justify-center p-8",
              "float-right"
            )}
          >
            <div className="relative w-full max-w-2xl">
              <KanbanSkeleton 
                {...kanbanProps} 
                columns={6} 
                items={12} 
                colorArray={initialColors} 
                colorScheme={endingColor} 
              />
            </div>
          </div>

          <div className={cn("w-1/2 relative", contentClassName)}>
            {sections.map((section, index) => (
              <div key={index} className="flex items-center relative" style={{ height: '75vh' }}>
                <div className="relative z-20 py-10 px-4 -ml-4 rounded">
                  {section}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      ref={containerRef}
      className={cn("relative", className)} 
      style={{ height: `${sections.length * 75}vh` }}
      {...props}
    >
      <div className={cn("relative", !fullWidth && "container mx-auto")}>        
        <div 
          className={cn(
            "sticky top-0 right-0 h-[75vh] w-full flex items-center justify-center p-8",
            "float-right"
          )}
        >
          <motion.div
            className="relative w-full max-w-2xl"
            layout
            transition={{ 
              type: "spring",
              stiffness: 100,
              damping: 25,
              mass: 0.8,
              restDelta: 0.001
            }}
          >
            <KanbanSkeleton {...currentKanbanProps} />
          </motion.div>
        </div>

        <div className={cn("w-1/2 relative", contentClassName)}>
          {sections.map((section, index) => {
            const currentIndex = getCurrentSectionIndex();
            const isActive = index === currentIndex;
            
            return (
              <div key={index} className="flex items-center relative" style={{ height: '75vh' }}>
                <motion.div 
                  className="relative z-20 py-10 px-4 -ml-4 rounded"
                  animate={{
                    opacity: isActive ? 1 : 0.5,
                    scale: isActive ? 1 : 0.98,
                  }}
                  transition={{
                    duration: 0.4,
                    ease: [0.25, 0.46, 0.45, 0.94] // Custom easing for smoother transitions
                  }}
                >
                  {section}
                </motion.div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export { ScrollArea, ScrollBar, ScrollImageFader, ScrollKanbanAnimator };
